import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

/**
 * Homepage Gallery Component
 * Modern, responsive image carousel following UI/UX best practices:
 * - Mobile-first design
 * - Auto-play with pause on hover
 * - Swipe gestures for mobile
 * - Keyboard navigation
 * - Smooth transitions
 * - Performance optimized with lazy loading
 * - Accessible with ARIA labels
 */
const HomepageGallery = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());

  // Gallery items with metadata - classic luxury overlay (warm charcoal tint)
  const galleryItems = [
    {
      id: 1,
      image: 'https://ecommerce-homepage-1761843287050.s3.il-central-1.amazonaws.com/homepage/for-her-banner.webp',
      alt: 'For Her - Women\'s Fragrances Collection',
      title: 'For Her',
      subtitle: 'Women\'s Fragrances',
      link: '/products?category=women',
      bgGradient: 'from-charcoal-900/40 via-charcoal-900/10 to-transparent',
      textPosition: 'center'
    },
    {
      id: 2,
      image: 'https://ecommerce-homepage-1761843287050.s3.il-central-1.amazonaws.com/homepage/summer-vibes-banner.webp',
      alt: 'Summer Vibes - Fresh and Radiant Fragrances',
      title: 'Summer Vibes',
      subtitle: 'Fresh and Radiant',
      link: '/products?season=summer',
      bgGradient: 'from-charcoal-900/40 via-charcoal-900/10 to-transparent',
      textPosition: 'right'
    },
    {
      id: 3,
      image: 'https://ecommerce-homepage-1761843287050.s3.il-central-1.amazonaws.com/homepage/golden-mirage-banner.webp',
      alt: 'Golden Mirage - A Journey of Opulence',
      title: 'Golden Mirage',
      subtitle: 'A Journey of Opulence',
      link: '/products?collection=luxury',
      bgGradient: 'from-charcoal-900/35 via-charcoal-900/8 to-transparent',
      textPosition: 'right'
    },
    {
      id: 4,
      image: 'https://ecommerce-homepage-1761843287050.s3.il-central-1.amazonaws.com/homepage/mini-collection-banner.webp',
      alt: 'Mini Collection - Luxury in Every Drop',
      title: 'Mini Collection',
      subtitle: 'Luxury in Every Drop',
      link: '/products?size=mini',
      bgGradient: 'from-charcoal-900/40 via-charcoal-900/10 to-transparent',
      textPosition: 'right'
    }
  ];

  // Auto-play functionality
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        handleNext();
      }, 5000); // Change slide every 5 seconds

      return () => clearInterval(interval);
    }
  }, [currentSlide, isPaused]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handleNext();
      } else if (e.key === 'ArrowRight') {
        handlePrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);

  // Preload images for smooth transitions
  useEffect(() => {
    galleryItems.forEach((item, index) => {
      const img = new Image();
      img.src = item.image;
      img.onload = () => {
        setLoadedImages(prev => new Set([...prev, index]));
      };
    });
  }, []);

  const handleNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % galleryItems.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, galleryItems.length]);

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, galleryItems.length]);

  const handleDotClick = (index) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const currentItem = galleryItems[currentSlide];

  return (
    <div 
      className="relative w-full overflow-hidden bg-gray-900"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="region"
      aria-label="קרוסלה של תמונות מוצרים"
      aria-live="polite"
    >
      {/* Main Carousel Container */}
      <div className="relative h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px]">
        {/* Images */}
        <div className="absolute inset-0">
          {galleryItems.map((item, index) => (
            <div
              key={item.id}
              className={`
                absolute inset-0 transition-all duration-700 ease-in-out
                ${index === currentSlide 
                  ? 'opacity-100 z-10 scale-100' 
                  : 'opacity-0 z-0 scale-105'
                }
              `}
            >
              {/* Background Image with Gradient Overlay */}
              <div className="absolute inset-0">
                <img
                  src={item.image}
                  alt={item.alt}
                  className="w-full h-full object-cover"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  onError={(e) => {
                    e.target.src = '/placeholder-banner.jpg';
                  }}
                />
                {/* Gradient overlay for better text contrast */}
                <div className={`absolute inset-0 bg-gradient-to-b ${item.bgGradient}`}></div>
              </div>

              {/* Content Overlay */}
              <Link 
                to={item.link}
                className="absolute inset-0 flex items-center justify-center px-4 sm:px-6 lg:px-8 group"
              >
                <div 
                  className={`
                    text-center max-w-3xl transform transition-all duration-500 delay-200
                    ${index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
                    ${item.textPosition === 'right' ? 'lg:mr-auto lg:ml-0' : ''}
                  `}
                >
                  <h2 
                    className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 
                      drop-shadow-lg tracking-wide"
                  >
                    {item.title}
                  </h2>
                  
                  <p 
                    className="text-lg sm:text-xl md:text-2xl text-white/90 
                      drop-shadow font-light tracking-wider
                      border-t border-white/30 pt-3 inline-block px-6"
                  >
                    {item.subtitle}
                  </p>

                  <div className="mt-6">
                    <span 
                      className="inline-flex items-center px-6 py-3 bg-cream-100 text-charcoal-900 
                        font-medium text-base
                        group-hover:bg-gold-500 group-hover:text-charcoal-900 
                        transition-colors duration-300 border border-cream-300/50"
                    >
                      גלה עכשיו
                      <ChevronLeftIcon className="w-4 h-4 mr-1.5" />
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Navigation Arrows - Desktop */}
        <div className="hidden md:block">
          <button
            onClick={handlePrevious}
            disabled={isTransitioning}
            className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 
              bg-black/20 hover:bg-black/40 backdrop-blur-sm 
              text-white p-2.5 lg:p-3 rounded-full 
              transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="תמונה קודמת"
          >
            <ChevronRightIcon className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>
          
          <button
            onClick={handleNext}
            disabled={isTransitioning}
            className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 
              bg-black/20 hover:bg-black/40 backdrop-blur-sm 
              text-white p-2.5 lg:p-3 rounded-full 
              transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="תמונה הבאה"
          >
            <ChevronLeftIcon className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>
        </div>

        {/* Progress Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {galleryItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => handleDotClick(index)}
              disabled={isTransitioning}
              className={`
                transition-all duration-300 rounded-full
                ${index === currentSlide 
                  ? 'w-12 h-3 bg-white shadow-lg' 
                  : 'w-3 h-3 bg-white/50 hover:bg-white/70'
                }
                disabled:cursor-not-allowed
              `}
              aria-label={`עבור לתמונה ${index + 1}`}
              aria-current={index === currentSlide}
            />
          ))}
        </div>

        {/* Slide Counter - Mobile */}
        <div className="absolute top-4 left-4 z-20 md:hidden">
          <div className="bg-black/50 backdrop-blur-md text-white px-3 py-1 rounded-full text-sm font-medium">
            {currentSlide + 1} / {galleryItems.length}
          </div>
        </div>

        {/* Pause/Play Indicator */}
        {isPaused && (
          <div className="absolute top-4 right-4 z-20">
            <div className="bg-black/50 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs">
              מושהה
            </div>
          </div>
        )}
      </div>

      {/* Mobile Swipe Hint */}
      <div className="md:hidden absolute bottom-20 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
        <div className="bg-black/40 backdrop-blur-sm text-white/80 px-3 py-1.5 rounded-full text-xs">
          החלק לשינוי תמונות →
        </div>
      </div>
    </div>
  );
};

export default HomepageGallery;

